import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import { authApi, authQueryKeys } from "@/modules/auth";
import { useDialog } from "@/shared/components/dialog";
import type { UserInfo } from "@/shared/types/user";
import { Button } from "@/shared/ui/button";

import { beginAccountConnection, type ConnectProvider } from "./connect";

interface AccountConnectionsProps {
  user: UserInfo;
}

const PROVIDERS = [
  { key: "wechat", label: "微信" },
  { key: "qq", label: "QQ" },
  { key: "apple", label: "Apple" },
  { key: "github", label: "GitHub" },
] as const;

type Provider = (typeof PROVIDERS)[number]["key"];

export function AccountConnections({ user }: AccountConnectionsProps) {
  const dialog = useDialog();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState<Provider | null>(null);

  async function bind(provider: ConnectProvider) {
    setBusy(provider);
    try {
      await beginAccountConnection(provider, user.id);
    } catch {
      setBusy(null);
    }
  }

  async function unbind(provider: Provider, label: string) {
    const confirmed = await dialog.confirm({
      title: `解除${label}绑定`,
      description: `解除后将无法继续使用${label}登录该账户。请确认已设置密码或保留其他登录方式。`,
      confirmText: "解除绑定",
      variant: "destructive",
    });
    if (!confirmed) return;
    setBusy(provider);
    try {
      if (provider === "wechat") await authApi.unbindWechat();
      else if (provider === "qq") await authApi.unbindQQ();
      else if (provider === "apple") await authApi.unbindApple();
      else await authApi.unbindGithub();
      await queryClient.invalidateQueries({ queryKey: authQueryKeys.userInfo });
      toast.success(`已解除${label}绑定`);
    } catch {
      // 请求层展示接口错误。
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="rounded-xl bg-[#f9f9f9] p-4 dark:bg-muted/50">
      <p className="text-xs leading-5 text-muted-foreground">绑定后可使用对应账号登录。</p>
      <div className="mt-3 divide-y divide-border/50">
        {PROVIDERS.map(({ key, label }) => {
          const profile = user[key];
          const bound = Boolean(profile);
          const detail = key === "wechat" || key === "qq"
            ? user[key]?.nickname
            : key === "apple" ? user.apple?.email : user.github?.login;
          const canBindHere = key === "wechat" || key === "qq";
          return (
            <div key={key} className="flex flex-wrap items-center justify-between gap-3 py-3">
              <div className="min-w-0">
                <p className="text-sm">{label}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {bound ? (detail || "已绑定") : canBindHere ? "未绑定" : "授权回调尚未接入当前 Web 站点"}
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="font-normal"
                disabled={busy !== null || (!bound && !canBindHere)}
                onClick={() => {
                  if (bound) void unbind(key, label);
                  else if (canBindHere) void bind(key);
                }}
              >
                {bound ? "解除绑定" : canBindHere ? "绑定" : "暂不可绑定"}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
